import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { logo } from '@clofo/ui/images'
import { Trans, useTranslation } from '@clofo/i18n'
import { usePageTitle } from '@clofo/ui/hooks'
import { LoadingSpinner } from '@clofo/ui/loading-spinner'
import { clofoApi } from '@clofo/data/api'
import { RegistrationSchema } from '@clofo/data/schemas'
import { RegistrationForm } from '../components/registration-form'

type Registration = z.infer<typeof RegistrationSchema>

/**
 * Landing page for the invitation email link: /registration?activationKey=…. The key is resolved
 * against the backend before the activation form is shown; an unknown key redirects to not-found
 * and a past-expiry invitation to expired.
 */
export const RegistrationPage = () => {
  const { t } = useTranslation()
  usePageTitle(t('login.registration.title'))
  const navigate = useNavigate()

  // Hash routing: the emailed link's params live after the # and are only readable via the
  // router — window.location.search would be empty.
  const [searchParams] = useSearchParams()
  const activationKey = searchParams.get('activationKey')

  const [registration, setRegistration] = useState<Registration | null>(null)

  useEffect(() => {
    if (!activationKey) return

    let cancelled = false
    const load = async () => {
      // Unlike the legacy app, no token refresh is fired here: the shared http client redirects
      // to login on a 401, which would hijack the invite link for anyone with a stale cookie.
      // Stale sessions are handled server-side — completing the registration denies outstanding
      // tokens and clears the auth cookies before the auto-login establishes the new session.
      try {
        const result = await clofoApi.users.getRegistration(activationKey)
        if (cancelled) return
        if (new Date(result.expiration) < new Date()) {
          navigate('/registration/expired', { replace: true })
          return
        }
        setRegistration(result)
      } catch {
        if (!cancelled) navigate('/registration/not-found', { replace: true })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [activationKey, navigate])

  if (!activationKey) return <Navigate to='/' replace />

  return (
    <main className='flex flex-col bg-background items-center justify-center'>
      <img src={logo} alt='Closing Folders' className='my-13 h-15' />
      <div className='w-full max-w-xl px-4 mb-13'>
        <h1 className='text-xl font-semibold mb-6'>{t('login.registration.title')}</h1>
        {registration ? (
          <>
            <p className='mb-8 leading-relaxed'>
              <Trans
                t={t}
                i18nKey='login.registration.description'
                values={{ email: registration.email }}
                components={{
                  emailLink: (
                    <a
                      className='text-primary font-medium hover:underline'
                      href={`mailto:${registration.email}`}
                    />
                  ),
                }}
              />
            </p>
            <RegistrationForm activationKey={activationKey} email={registration.email} />
          </>
        ) : (
          <div className='flex justify-center py-8' aria-busy='true' aria-live='polite'>
            <LoadingSpinner />
          </div>
        )}
      </div>
    </main>
  )
}
